/**
 * 003 — Abdeckung (US1, FR-002/FR-003): per own account the imported
 * statements, the uncovered date ranges, and the opening/closing balance per
 * month and quarter. A balance no statement backs reads "Nicht belegt"; a gap
 * on one account never marks another account as incomplete.
 */
import { useQuery } from '@tanstack/react-query';
import { EmptyState, GlassPanel, LoadingSpinner, StatusBadge } from '../ui';
import { bankApi, bankKeys, day, ibanLabel, money, type CoverageResponse } from '../../lib/bank-api';
import { errorText } from './ReviewEditor';
import type { BankTabProps } from './types';

type AccountCoverage = CoverageResponse['accounts'][number];

function AccountCard({ data, selected }: { data: AccountCoverage; selected: boolean }) {
  const complete = data.gaps.length === 0;
  return (
    <GlassPanel data-testid="coverage-account" aria-current={selected ? 'true' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold break-all">{ibanLabel(data.account)}</h2>
          {data.label && data.label !== ibanLabel(data.account) && <p className="text-sm text-white/60">{data.label}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {selected && (
            <StatusBadge tone="info" data-testid="coverage-account-selected">
              Ausgewählt
            </StatusBadge>
          )}
          <StatusBadge tone={complete ? 'success' : 'warning'}>{complete ? 'Vollständig' : 'Lücken'}</StatusBadge>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-white/60">Kontoauszüge</dt>
          <dd className="font-semibold">{data.statements}</dd>
        </div>
        <div>
          <dt className="text-white/60">Erster Auszug ab</dt>
          <dd className="font-semibold">{day(data.firstDate)}</dd>
        </div>
        <div>
          <dt className="text-white/60">Letzter Auszug bis</dt>
          <dd className="font-semibold">{day(data.lastDate)}</dd>
        </div>
      </dl>
      <section className="mt-4" aria-label="Nicht belegte Zeiträume">
        <h3 className="font-medium">Nicht belegte Zeiträume</h3>
        {complete ? (
          <p className="text-sm text-white/70">Keine Lücken</p>
        ) : (
          <ul className="text-sm text-amber-200">
            {data.gaps.map((g) => (
              <li key={`${g.from}:${g.to}`} data-testid="coverage-gap">
                {`Lücke ${day(g.from)} – ${day(g.to)}`}
              </li>
            ))}
          </ul>
        )}
      </section>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <section>
          <h3 className="font-medium">Monate</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left">Monat</th>
                  <th className="text-right">Anfang</th>
                  <th className="text-right">Ende</th>
                  <th className="text-left">Abdeckung</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => (
                  <tr key={m.month}>
                    <td>{m.month}</td>
                    <td className="text-right">{money(m.openingCents)}</td>
                    <td className="text-right">{money(m.closingCents)}</td>
                    <td>
                      <StatusBadge tone={m.complete ? 'success' : 'warning'}>
                        {m.complete ? 'Vollständig' : 'Lücke'}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.months.length === 0 && <p className="text-sm text-white/50">Keine Monate im Zeitraum.</p>}
        </section>
        <section>
          <h3 className="font-medium">Quartale</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left">Quartal</th>
                  <th className="text-right">Anfangssaldo</th>
                  <th className="text-right">Endsaldo</th>
                  <th className="text-left">Abdeckung</th>
                </tr>
              </thead>
              <tbody>
                {data.quarters.map((q) => (
                  <tr key={q.quarter}>
                    <td>{q.quarter}</td>
                    <td className="text-right">{money(q.openingCents)}</td>
                    <td className="text-right">{money(q.closingCents)}</td>
                    <td>
                      <StatusBadge tone={q.complete ? 'success' : 'warning'}>
                        {q.complete ? 'Vollständig' : 'Lücke'}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.quarters.length === 0 && <p className="text-sm text-white/50">Keine Quartale im Zeitraum.</p>}
        </section>
      </div>
    </GlassPanel>
  );
}

export function CoverageTab(props: BankTabProps) {
  const query = useQuery({
    queryKey: bankKeys.coverage(props.tenantId, props.from, props.to),
    queryFn: () => bankApi.coverage({ from: props.from, to: props.to }),
    enabled: Boolean(props.tenantId),
  });
  if (query.isLoading) return <LoadingSpinner />;
  if (query.error)
    return (
      <p role="alert" className="rounded border border-red-400/40 p-3">
        {errorText(query.error)}
      </p>
    );
  if (!query.data) return null;
  const accounts = query.data.accounts;
  if (accounts.length === 0) return <EmptyState title="Noch keine Kontoauszüge importiert." />;
  const selected = props.account ? accounts.find((a) => a.account === props.account) : undefined;
  const ordered = selected ? [selected, ...accounts.filter((a) => a !== selected)] : accounts;
  return (
    <div className="space-y-6">
      <p className="text-sm text-white/70">
        Abdeckung {day(props.from)} – {day(props.to)} je Konto. Salden ohne zugrunde liegenden Kontoauszug werden als
        „Nicht belegt“ ausgewiesen; eine Lücke auf einem Konto betrifft die anderen Konten nicht.
      </p>
      {ordered.map((a) => (
        <AccountCard key={a.account} data={a} selected={a === selected} />
      ))}
    </div>
  );
}
